/**
 * Broker Commission Calculation (exchange-keyed schedule model)
 *
 * Computes the commission for a single trade leg from the broker's
 * CommissionSchedule for that (exchange, instrumentClass) cell, applying the
 * "whichever is greater" rule over the populated fee components and adding GST
 * on top when the schedule enables it. Also selects the schedule for a leg from
 * a broker profile.
 *
 * Validates: Requirements 2, 3, 4, 5, 9
 */

import { BrokerProfile, CommissionSchedule } from '../types/entities';
import { Exchange, InstrumentClass } from '../types/enums';

/** 1 option contract = this many underlying shares. */
export const OPTION_CONTRACT_MULTIPLIER = 100;

export interface CommissionInput {
  /** Trade value for the leg in native currency (price × quantity or premium × quantity). */
  tradeValue: number;
  /** Share quantity for the leg. Units are derived from this + instrumentClass. */
  quantity: number;
}

export interface CommissionResult {
  /** Net commission (max of populated components), rounded to 2 dp. */
  commission: number;
  /** GST amount, rounded to 2 dp. 0 when the schedule disables GST. */
  gst: number;
  /** commission + gst, rounded to 2 dp. Effective cost to the investor. */
  total: number;
}

/** Round a number to 2 decimal places. */
function round2(x: number): number {
  return Math.round(x * 100) / 100;
}

/**
 * Compute the commission for one trade leg from its schedule.
 *
 * `units` is `quantity` for SHARES and `quantity / OPTION_CONTRACT_MULTIPLIER`
 * for OPTIONS. `commission` is the rounded maximum over only the populated fee
 * components (flat if > 0, percentage if `percentageRate` > 0, per-unit if
 * `perUnitFee` present and > 0). GST is applied on top when enabled.
 *
 * @param schedule - The commission schedule for the leg's (exchange, instrumentClass)
 * @param input - The leg's trade value and share quantity
 * @returns `{ commission, gst, total }`, each rounded to 2 decimal places
 */
export function computeCommission(
  schedule: CommissionSchedule,
  input: CommissionInput,
): CommissionResult {
  const units =
    schedule.instrumentClass === InstrumentClass.OPTIONS
      ? input.quantity / OPTION_CONTRACT_MULTIPLIER
      : input.quantity;

  const candidates: number[] = [];
  if (schedule.flatFee > 0) candidates.push(schedule.flatFee);
  if (schedule.percentageRate > 0) {
    candidates.push((schedule.percentageRate / 100) * input.tradeValue);
  }
  if (schedule.perUnitFee !== undefined && schedule.perUnitFee > 0) {
    candidates.push(schedule.perUnitFee * units);
  }

  // Validation guarantees at least one populated component; guard defensively.
  const commission = round2(candidates.length > 0 ? Math.max(...candidates) : 0);
  const gst = schedule.gstEnabled ? round2(commission * (schedule.gstRate / 100)) : 0;
  const total = round2(commission + gst);

  return { commission, gst, total };
}

/**
 * Compute the option exercise/assignment fee for a schedule.
 *
 * This fee is ADDITIVE and applies only on exercise/assignment (it is NOT part
 * of the flat/percentage/per-unit "greater of" trading commission). It equals
 * `exerciseFeePerContract × contracts`, with GST added on top when the schedule
 * enables it. Returns all zeros when the schedule has no exercise fee.
 *
 * On ASX, IBKR charges AUD 0.275/contract for stock-option exercise/assignment;
 * most other markets charge nothing.
 *
 * @param schedule - The OPTIONS schedule for the leg's (exchange, OPTIONS)
 * @param contracts - Number of option contracts exercised/assigned
 * @returns `{ commission, gst, total }`, each rounded to 2 decimal places
 */
export function computeExerciseFee(
  schedule: CommissionSchedule,
  contracts: number,
): CommissionResult {
  const rate = schedule.exerciseFeePerContract;
  if (rate === undefined || rate <= 0 || contracts <= 0) {
    return { commission: 0, gst: 0, total: 0 };
  }
  const commission = round2(rate * contracts);
  const gst = schedule.gstEnabled ? round2(commission * (schedule.gstRate / 100)) : 0;
  const total = round2(commission + gst);
  return { commission, gst, total };
}

/**
 * Select the schedule for a `(exchange, instrumentClass)` pair from a profile.
 *
 * @param profile - The broker profile whose schedules are searched
 * @param exchange - The exchange to match
 * @param instrumentClass - The instrument class to match
 * @returns The unique matching schedule, or `undefined` when none exists. Does
 *          not mutate the profile.
 */
export function selectSchedule(
  profile: BrokerProfile,
  exchange: Exchange,
  instrumentClass: InstrumentClass,
): CommissionSchedule | undefined {
  return profile.schedules.find(
    (s) => s.exchange === exchange && s.instrumentClass === instrumentClass,
  );
}
